import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/language';
import { logger } from '@/utils/logger';
import { SESSION_CONFIG } from '@/config/session';

interface SessionState {
  isActive: boolean;
  expiresAt: number | null;
  minutesUntilExpiry: number | null;
  showWarning: boolean;
}

const SESSION_CHECK_INTERVAL = SESSION_CONFIG.CHECK_INTERVAL_MS;
const WARNING_THRESHOLD_MINUTES = SESSION_CONFIG.WARNING_THRESHOLD_MINUTES;
const CRITICAL_THRESHOLD_MINUTES = SESSION_CONFIG.CRITICAL_THRESHOLD_MINUTES;

export const useSessionMonitor = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [sessionState, setSessionState] = useState<SessionState>({
    isActive: false,
    expiresAt: null,
    minutesUntilExpiry: null,
    showWarning: false,
  });

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Calculate minutes until session expiry
  const calculateMinutesUntilExpiry = useCallback((expiresAt: number): number => {
    const now = Date.now() / 1000; // Convert to seconds
    const secondsRemaining = expiresAt - now;
    return Math.floor(secondsRemaining / 60);
  }, []);

  // Check session status
  const checkSession = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        logger.error('Session check error:', error);
        setSessionState({
          isActive: false,
          expiresAt: null,
          minutesUntilExpiry: null,
          showWarning: false,
        });
        return;
      }

      if (!session) {
        setSessionState({
          isActive: false,
          expiresAt: null,
          minutesUntilExpiry: null,
          showWarning: false,
        });
        return;
      }

      const expiresAt = session.expires_at || 0;
      const minutesUntilExpiry = calculateMinutesUntilExpiry(expiresAt);

      setSessionState({
        isActive: true,
        expiresAt,
        minutesUntilExpiry,
        showWarning: minutesUntilExpiry <= WARNING_THRESHOLD_MINUTES,
      });

      // Session expired - redirect to login
      if (minutesUntilExpiry <= 0 && !isRedirecting) {
        setIsRedirecting(true);
        logger.info('Session expired, redirecting to login');

        // Sign out the user
        await supabase.auth.signOut();

        // Show simple expiration message
        toast({
          title: t('auth.sessionExpired', 'Session expired'),
          description: t('auth.sessionExpiredMessage', 'Your session has expired. Please log in again.'),
          variant: 'default',
          duration: 3000,
        });

        // Redirect to login page after a brief delay
        setTimeout(() => {
          window.location.href = '/auth?mode=login&reason=session_expired';
        }, 1000);

        return;
      }
    } catch (error) {
      logger.error('Unexpected session check error:', error);
    }
  }, [calculateMinutesUntilExpiry, toast, t, isRedirecting]);

  // Refresh session manually
  const refreshSession = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();

      if (error) {
        logger.error('Session refresh error:', error);
        toast({
          title: t('auth.sessionRefreshFailed', 'Session refresh failed'),
          description: t('auth.sessionRefreshErrorMessage', 'Please log in again.'),
          variant: 'destructive',
        });
        return false;
      }

      if (session) {
        logger.info('Session refreshed successfully');
        toast({
          title: t('auth.sessionRefreshed', 'Session refreshed'),
          description: t('auth.sessionRefreshedMessage', 'You can continue working.'),
          variant: 'default',
        });
        setIsRedirecting(false);
        return true;
      }

      return false;
    } catch (error) {
      logger.error('Unexpected session refresh error:', error);
      return false;
    }
  }, [toast, t]);

  // Set up session monitoring
  useEffect(() => {
    // Initial check
    checkSession();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.info('Auth state changed:', event);

      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setSessionState({
          isActive: false,
          expiresAt: null,
          minutesUntilExpiry: null,
          showWarning: false,
        });
        setIsRedirecting(false);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setIsRedirecting(false);
        checkSession();
      }
    });

    return () => {
      clearInterval(intervalId);
      subscription.unsubscribe();
    };
  }, [checkSession]);

  return {
    sessionState,
    refreshSession,
    checkSession,
  };
};
